import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, MapPin, Plus, TrendingUp, Receipt, X, User, Car, Clock, DollarSign, Loader2, Calculator, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SERVICE_TYPES = [
  { value: 'concierge', label: 'Concierge' },
  { value: 'exotic', label: 'Exotic' },
  { value: 'group', label: 'Group' },
  { value: 'airport', label: 'Airport' },
  { value: 'rental', label: 'Self-Drive Rental' },
];

const RevenueCalendarTab = ({ onQuickQuote }) => {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState({ bookings: [], total: 0, revenue: 0, deposits: 0, busiest_day: null, by_day: {}, daily_pl: {}, quotes: [], quoted_total: 0, quotes_by_day: {} });
  const [selectedDay, setSelectedDay] = useState(null);
  const [quickAddDay, setQuickAddDay] = useState(null);  // opens modal when set

  const reload = () => {
    fetch(`${API}/api/command-center/calendar?month=${month}&year=${year}`).then(r => r.json()).then(setData).catch(() => {});
  };

  useEffect(reload, [month, year]);

  const prev = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  // Calendar grid
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const dayBookings = selectedDay ? (data.by_day[String(selectedDay)] || []) : [];
  const dayPL = selectedDay ? (data.daily_pl?.[String(selectedDay)] || null) : null;
  const dayQuotes = selectedDay ? (data.quotes_by_day?.[String(selectedDay)] || []) : [];
  const dateKeyFor = (day) => `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const monthlyExpenses = Object.values(data.daily_pl || {}).reduce((s, d) => s + (d.expenses || 0), 0);
  const monthlyNet = (data.revenue || 0) - monthlyExpenses;

  const openQuickAdd = (day) => {
    if (onQuickQuote) { onQuickQuote(dateKeyFor(day)); return; }
    setQuickAddDay(day);
  };

  const [convertingId, setConvertingId] = useState(null);
  const convertQuote = async (q) => {
    if (!window.confirm(`Convert quote for ${q.client_name || 'client'} (${f(q.grand_total || q.total)}) into a confirmed booking?`)) return;
    setConvertingId(q.quote_id);
    try {
      const r = await fetch(`${API}/api/command-center/quote-log/${q.quote_id}/convert`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || 'Conversion failed');
      toast.success(d.already_converted ? `Already booked — ${d.booking_id}` : `Quote booked — ${d.booking_id}`);
      reload();
    } catch (e) { toast.error(e.message); }
    setConvertingId(null);
  };

  const handleQuickAddSaved = () => {
    setQuickAddDay(null);
    reload();
  };

  return (
    <div data-testid="revenue-calendar-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><CalendarIcon className="h-5 w-5 text-red-600" /> Bookings & Quotes Calendar</h2>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prev} className="h-8 w-8 p-0"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-bold min-w-[140px] text-center">{MONTHS[month - 1]} {year}</span>
          <Button variant="outline" size="sm" onClick={next} className="h-8 w-8 p-0"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Stats row — booked revenue + pending quotes shown separately */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-gray-900">{data.total || 0}</p><p className="text-[10px] text-gray-500">Trips</p></Card>
        <Card className="p-3 border-gray-200 text-center" data-testid="cal-stat-booked"><p className="text-lg font-bold text-red-600">{f(data.revenue)}</p><p className="text-[10px] text-gray-500">Booked</p></Card>
        <Card className="p-3 border-amber-200 bg-amber-50/50 text-center" data-testid="cal-stat-quoted"><p className="text-lg font-bold text-amber-600">{f(data.quoted_total)}</p><p className="text-[10px] text-amber-700">{(data.quotes || []).length} Quote{(data.quotes || []).length !== 1 ? 's' : ''} Pending</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-amber-600">{f(monthlyExpenses)}</p><p className="text-[10px] text-gray-500">Expenses</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className={`text-lg font-bold ${monthlyNet >= 0 ? 'text-emerald-600' : 'text-red-700'}`}>{f(monthlyNet)}</p><p className="text-[10px] text-gray-500">Net</p></Card>
        <Card className="p-3 border-gray-200 text-center"><p className="text-lg font-bold text-emerald-600">{f(data.deposits)}</p><p className="text-[10px] text-gray-500">Deposits</p></Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Calendar Grid */}
        <div className="lg:col-span-2">
          <Card className="p-4 border-gray-200">
            <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
              {DAYS.map(d => <div key={d} className="bg-gray-50 p-2 text-center text-[10px] font-bold text-gray-500">{d}</div>)}
              {cells.map((day, i) => {
                if (!day) return <div key={`e${i}`} className="bg-white p-2 min-h-[64px]" />;
                const dayData = data.by_day[String(day)] || [];
                const dayQ = data.quotes_by_day?.[String(day)] || [];
                const hasBookings = dayData.length > 0;
                const isSelected = selectedDay === day;
                const isToday = day === now.getDate() && month === now.getMonth() + 1 && year === now.getFullYear();
                const pl = data.daily_pl?.[String(day)];
                const dayRevenue = pl?.revenue ?? dayData.reduce((s, b) => s + (b.total || 0), 0);
                const dayExp = pl?.expenses ?? 0;
                const dayNet = pl?.net ?? (dayRevenue - dayExp);
                return (
                  <div key={day} className={`bg-white p-1.5 min-h-[64px] relative group ${isSelected ? 'ring-2 ring-red-500 bg-red-50' : ''}`} data-testid={`cal-day-${day}`}>
                    <button onClick={() => setSelectedDay(day === selectedDay ? null : day)} className="absolute inset-0 hover:bg-gray-50/50 rounded transition-all" aria-label={`Day ${day}`} />
                    <div className="relative pointer-events-none">
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${isToday ? 'bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center' : 'text-gray-700'}`}>{day}</span>
                      </div>
                      {hasBookings && (
                        <div className="mt-0.5 space-y-0.5">
                          <div className="bg-red-100 text-red-700 rounded px-1 py-0.5 text-[8px] font-bold truncate">{dayData.length} trip{dayData.length > 1 ? 's' : ''}</div>
                          {dayRevenue > 0 && <div className="text-[8px] text-emerald-600 font-semibold">{f(dayRevenue)}</div>}
                          {dayExp > 0 && <div className="text-[8px] text-amber-600">−{f(dayExp)}</div>}
                          {(dayRevenue > 0 || dayExp > 0) && <div className={`text-[8px] font-bold ${dayNet >= 0 ? 'text-gray-900' : 'text-red-700'}`}>={f(dayNet)}</div>}
                        </div>
                      )}
                      {dayQ.length > 0 && (
                        <div className="mt-0.5">
                          <div className="bg-amber-100 text-amber-700 rounded px-1 py-0.5 text-[8px] font-bold truncate">{dayQ.length} quote{dayQ.length > 1 ? 's' : ''} · {f(dayQ.reduce((s, q) => s + (q.grand_total || q.total || 0), 0))}</div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); openQuickAdd(day); }}
                      className="absolute bottom-1 right-1 h-5 w-5 rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 hover:bg-red-700 flex items-center justify-center transition-all"
                      title="Quick-add booking"
                      data-testid={`cal-add-${day}`}
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        {/* Day Detail */}
        <div>
          {selectedDay ? (
            <Card className="p-4 border-gray-200">
              <h3 className="text-sm font-bold text-gray-900 mb-3">{MONTHS[month - 1]} {selectedDay}, {year}</h3>
              {dayPL && (dayPL.revenue || dayPL.expenses) > 0 && (
                <div className="grid grid-cols-3 gap-1.5 mb-3 text-center">
                  <div className="bg-emerald-50 rounded p-2"><p className="text-[9px] text-emerald-700 uppercase tracking-wider flex items-center justify-center gap-1"><TrendingUp className="h-2.5 w-2.5" /> In</p><p className="text-xs font-bold text-emerald-700">{f(dayPL.revenue)}</p></div>
                  <div className="bg-amber-50 rounded p-2"><p className="text-[9px] text-amber-700 uppercase tracking-wider flex items-center justify-center gap-1"><Receipt className="h-2.5 w-2.5" /> Out</p><p className="text-xs font-bold text-amber-700">{f(dayPL.expenses)}</p></div>
                  <div className={`rounded p-2 ${dayPL.net >= 0 ? 'bg-gray-100' : 'bg-red-50'}`}><p className="text-[9px] text-gray-700 uppercase tracking-wider">Net</p><p className={`text-xs font-bold ${dayPL.net >= 0 ? 'text-gray-900' : 'text-red-700'}`}>{f(dayPL.net)}</p></div>
                </div>
              )}
              {dayBookings.length === 0 ? (
                <p className="text-xs text-gray-400 mb-3">No trips scheduled</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {dayBookings.map((b, i) => {
                    const tf = b.pickup_time && b.dropoff_time ? `${b.pickup_time}–${b.dropoff_time}` : (b.pickup_time || b.pickup_time_str || '');
                    return (
                      <div key={i} className="p-2 rounded-lg bg-gray-50 border border-gray-100" data-testid={`day-booking-${i}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-gray-900">{b.client_name}</span>
                          <span className="text-xs font-bold text-red-600">{f(b.total)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                          {tf && <span className="bg-red-50 text-red-700 px-1.5 py-0.5 rounded font-bold">{tf}</span>}
                          {b.vehicle && <span className="truncate max-w-[140px]">{b.vehicle}</span>}
                          {b.hours && <span>{b.hours}hr</span>}
                          {b.source && <span className="ml-auto bg-gray-200 px-1.5 rounded text-[9px]">{b.source === 'cc_bookings' ? 'CC' : 'public'}</span>}
                        </div>
                        {b.pickup_address && <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{b.pickup_address}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
              {dayQuotes.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-1.5 flex items-center gap-1"><FileText className="h-3 w-3" /> Pending Quotes</p>
                  <div className="space-y-2">
                    {dayQuotes.map((q, i) => (
                      <div key={i} className="p-2 rounded-lg bg-amber-50 border border-amber-100" data-testid={`day-quote-${i}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-gray-900">{q.client_name || '—'}</span>
                          <span className="text-xs font-bold text-amber-600">{f(q.grand_total || q.total)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                          {q.vehicle_name && <span className="truncate max-w-[140px]">{q.vehicle_name}</span>}
                          {q.hours ? <span>{q.hours}hr</span> : null}
                          <span className="ml-auto bg-amber-200 text-amber-800 px-1.5 rounded text-[9px] uppercase font-bold">{q.status || 'sent'}</span>
                        </div>
                        <button
                          onClick={() => convertQuote(q)}
                          disabled={convertingId === q.quote_id}
                          className="mt-1.5 w-full text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded py-1 flex items-center justify-center gap-1 disabled:opacity-50"
                          data-testid={`convert-quote-${i}`}
                        >
                          {convertingId === q.quote_id ? <Loader2 className="h-3 w-3 animate-spin" /> : '✓'} Convert to Booking
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Button onClick={() => openQuickAdd(selectedDay)} size="sm" className="w-full bg-red-600 hover:bg-red-700 text-xs h-8" data-testid="quick-add-from-detail">
                  <Plus className="h-3 w-3 mr-1" /> Quick Add Booking
                </Button>
                {onQuickQuote && (
                  <Button onClick={() => onQuickQuote(dateKeyFor(selectedDay))} size="sm" variant="outline" className="w-full border-amber-400 text-amber-700 hover:bg-amber-50 text-xs h-8" data-testid="quick-quote-from-detail">
                    <Calculator className="h-3 w-3 mr-1" /> Quick Quote This Day
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <Card className="p-6 border-gray-200 text-center">
              <CalendarIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-400 mb-2">Click a day to see trip details + P&L</p>
              <p className="text-[10px] text-gray-400">Hover any day → click <Plus className="h-3 w-3 inline -mt-0.5" /> to quick-add a booking</p>
            </Card>
          )}
        </div>
      </div>

      {quickAddDay !== null && (
        <QuickBookingModal
          day={quickAddDay}
          month={month}
          year={year}
          onClose={() => setQuickAddDay(null)}
          onSaved={handleQuickAddSaved}
        />
      )}
    </div>
  );
};

// ─── QuickBookingModal — replaces 5 chained window.prompt() calls ──────────
const QuickBookingModal = ({ day, month, year, onClose, onSaved }) => {
  const dateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [clientQuery, setClientQuery] = useState('');
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
    pickupTime: '',
    dropoffTime: '',
    pickup_address: '',
    dropoff_address: '',
    vehicle: '',
    vehicleId: '',
    service_type: 'concierge',
    hours: '',
    total: '',
    deposit: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Load clients + fleet on open
  useEffect(() => {
    fetch(`${API}/api/command-center/clients?segment=all`).then(r => r.json()).then(d => setClients(d.clients || [])).catch(() => {});
    fetch(`${API}/api/pricing/vehicles`).then(r => r.json()).then(d => setVehicles(d.vehicles || d || [])).catch(() => {});
  }, []);

  // Auto-compute hours from pickup/dropoff
  useEffect(() => {
    if (!form.pickupTime || !form.dropoffTime) return;
    const [ph, pm] = form.pickupTime.split(':').map(Number);
    const [dh, dm] = form.dropoffTime.split(':').map(Number);
    if (Number.isNaN(ph) || Number.isNaN(dh)) return;
    let diff = (dh * 60 + (dm || 0)) - (ph * 60 + (pm || 0));
    if (diff < 0) diff += 24 * 60;  // overnight
    const h = (diff / 60).toFixed(1).replace(/\.0$/, '');
    setForm(f => ({ ...f, hours: h }));
  }, [form.pickupTime, form.dropoffTime]);

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients.slice(0, 6);
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').replace(/\D/g, '').includes(q.replace(/\D/g, '')) ||
      (c.email || '').toLowerCase().includes(q)
    ).slice(0, 8);
  }, [clients, clientQuery]);

  const pickClient = (c) => {
    setForm(f => ({ ...f, client_name: c.name || '', client_phone: c.phone || '', client_email: c.email || '' }));
    setClientQuery(c.name || '');
    setShowClientPicker(false);
  };

  const pickVehicle = (vid) => {
    const v = vehicles.find(x => x.vehicle_id === vid);
    setForm(f => ({ ...f, vehicleId: vid, vehicle: v ? v.vehicle_name : '' }));
  };

  const balance = (parseFloat(form.total) || 0) - (parseFloat(form.deposit) || 0);

  const save = async () => {
    if (!form.client_name.trim()) { toast.error('Client name required'); return; }
    if (!form.total || parseFloat(form.total) <= 0) { toast.error('Total amount required'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/command-center/confirmed-bookings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_date: dateKey, pickupDate: dateKey,
          pickupTime: form.pickupTime, dropoffTime: form.dropoffTime,
          pickup_address: form.pickup_address, dropoff_address: form.dropoff_address,
          client_name: form.client_name.trim(),
          client_phone: form.client_phone.trim(),
          client_email: form.client_email.trim(),
          total: parseFloat(form.total) || 0,
          deposit: parseFloat(form.deposit) || 0,
          hours: parseFloat(form.hours) || 0,
          vehicle: form.vehicle, vehicleId: form.vehicleId,
          service_type: form.service_type,
          notes: form.notes,
          cc_status: 'upcoming',
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      toast.success(`Booking saved for ${form.client_name} on ${MONTHS[month - 1]} ${day}`);
      onSaved();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4" onClick={onClose} data-testid="quick-booking-modal">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold">Quick-Add Booking</p>
            <h3 className="text-lg font-black tracking-tight">{MONTHS[month - 1]} {day}, {year}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black" data-testid="qb-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Service type pills */}
          <div>
            <Label icon={Car}>Service</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {SERVICE_TYPES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setForm({ ...form, service_type: s.value })}
                  className={`px-3 py-1 text-xs rounded-full font-semibold border transition-colors ${form.service_type === s.value ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                  data-testid={`qb-service-${s.value}`}
                >{s.label}</button>
              ))}
            </div>
          </div>

          {/* Client picker with autocomplete */}
          <div className="relative">
            <Label icon={User}>Client *</Label>
            <Input
              value={clientQuery || form.client_name}
              onChange={e => { setClientQuery(e.target.value); setForm({ ...form, client_name: e.target.value }); setShowClientPicker(true); }}
              onFocus={() => setShowClientPicker(true)}
              placeholder="Start typing name, phone, or email…"
              className="mt-1 h-10"
              data-testid="qb-client-name"
            />
            {showClientPicker && filteredClients.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto" data-testid="qb-client-picker">
                {filteredClients.map((c, i) => (
                  <button
                    key={(c.client_id || c.id || i) + '-' + i}
                    type="button"
                    onClick={() => pickClient(c)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 border-b last:border-b-0"
                  >
                    <div className="font-semibold">{c.name || '—'}</div>
                    <div className="text-[10px] text-gray-500">{c.phone || ''} {c.email ? ` · ${c.email}` : ''}</div>
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setShowClientPicker(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-blue-600 hover:bg-blue-50"
                >+ Use "{clientQuery || form.client_name}" as new client</button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input value={form.client_phone} onChange={e => setForm({ ...form, client_phone: e.target.value })} placeholder="(404) 555-0100" className="mt-1 h-10" data-testid="qb-client-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.client_email} onChange={e => setForm({ ...form, client_email: e.target.value })} placeholder="client@email.com" className="mt-1 h-10" />
            </div>
          </div>

          {/* Vehicle dropdown */}
          <div>
            <Label icon={Car}>Vehicle</Label>
            <select
              value={form.vehicleId}
              onChange={e => pickVehicle(e.target.value)}
              className="mt-1 w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white"
              data-testid="qb-vehicle"
            >
              <option value="">— Pick a vehicle or type below —</option>
              {vehicles.map(v => (
                <option key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_name} · {v.category}</option>
              ))}
            </select>
            <Input
              value={form.vehicle}
              onChange={e => setForm({ ...form, vehicle: e.target.value })}
              placeholder="…or type a custom vehicle / service"
              className="mt-2 h-9 text-xs"
            />
          </div>

          {/* Times */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label icon={Clock}>Pickup time</Label>
              <Input type="time" value={form.pickupTime} onChange={e => setForm({ ...form, pickupTime: e.target.value })} className="mt-1 h-10" data-testid="qb-pickup-time" />
            </div>
            <div>
              <Label icon={Clock}>Drop-off time</Label>
              <Input type="time" value={form.dropoffTime} onChange={e => setForm({ ...form, dropoffTime: e.target.value })} className="mt-1 h-10" />
            </div>
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label icon={MapPin}>Pickup address</Label>
              <Input value={form.pickup_address} onChange={e => setForm({ ...form, pickup_address: e.target.value })} className="mt-1 h-10" placeholder="123 Main St, Atlanta GA" />
            </div>
            <div>
              <Label icon={MapPin}>Drop-off address</Label>
              <Input value={form.dropoff_address} onChange={e => setForm({ ...form, dropoff_address: e.target.value })} className="mt-1 h-10" placeholder="Hartsfield-Jackson, Atlanta" />
            </div>
          </div>

          {/* $$$ */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Hours</Label>
              <Input type="number" step="0.5" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} className="mt-1 h-10" />
            </div>
            <div>
              <Label icon={DollarSign}>Total *</Label>
              <Input type="number" step="0.01" value={form.total} onChange={e => setForm({ ...form, total: e.target.value })} className="mt-1 h-10" data-testid="qb-total" />
            </div>
            <div>
              <Label>Deposit</Label>
              <Input type="number" step="0.01" value={form.deposit} onChange={e => setForm({ ...form, deposit: e.target.value })} className="mt-1 h-10" />
            </div>
          </div>
          {form.total && (
            <div className="flex justify-between text-xs bg-gray-50 px-3 py-2 rounded">
              <span className="text-gray-500">Balance due after deposit</span>
              <span className="font-bold">{f(balance)}</span>
            </div>
          )}

          <div>
            <Label>Notes</Label>
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="mt-1 h-10" placeholder="Driver, special requests, etc." />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2 sticky bottom-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="qb-save">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</> : 'Save Booking'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Label = ({ icon: Icon, children }) => (
  <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500 flex items-center gap-1">
    {Icon && <Icon className="h-3 w-3" />} {children}
  </label>
);

export default RevenueCalendarTab;
